import { useEffect, useRef, useState } from "react";
import { getScaleDisplayName, isScaleCompatibleWith, getScaleForSampleName } from "$utils/music"; // music theory related
import { PresetDropdown } from "../components/PresetDropdown";
import { sampleLibrary} from "../sampleLibrary";
import { useDispatch, useSelector } from "react-redux";
import { loadPresetInInstrument, savePreset, deletePreset, selectPresets, selectTonality } from "../store";
import { SaveIcon, MenuIcon } from "lucide-react";
import { Dialog } from "primereact/dialog";
import { Menu } from '@base-ui/react/menu';
import { downloadObjectAsJson } from "../utils/misc";

export function PresetBankBar({className, instrument}) {
    
    const currentPresetID = useSelector(instrument.getDisplayParameterSelector("presetID"));
    const presetBank = {...useSelector(selectPresets)};
    presetBank.init = {_metadata: { name: "Init", isFactory: true, instrumentType: instrument.metadata.tipus }}
    const currentPreset = presetBank[currentPresetID];
    const savedPresetsForInstrumentType = Object.fromEntries(Object.entries(presetBank)
        .filter(([_id, {_metadata: {instrumentType, isUnsaved}}]) => !isUnsaved && instrumentType === instrument.metadata.tipus)
    );

    const dispatch = useDispatch();
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    return <>
        <div className={`${className} flex`}>
            <PresetMenu data-widget-id="presetMenuTrigger" currentPresetID={currentPresetID} currentPreset={currentPreset} instrumentID={instrument.id} showNamePresetDialog={() => setIsDialogVisible(true)}/>
            <PresetDropdown
                data-widget-id="presetSelect"
                currentPresetID={currentPresetID}
                onChange={(evt) => dispatch(loadPresetInInstrument({instrumentID: instrument.id, presetID: evt.target.value}))}
                instrumentType={instrument.metadata.tipus}
                hideInit
                />
            <button data-widget-id="presetSaveButton" className="btn btn-white w-30" onClick={() => setIsDialogVisible(true)} disabled={!currentPreset._metadata.isUnsaved}><SaveIcon/></button>
        </div>
        <NamePresetDialog isVisible={isDialogVisible} setIsVisible={setIsDialogVisible} presetName={currentPreset._metadata.name} presetID={currentPresetID} savedPresetsForInstrumentType={savedPresetsForInstrumentType}/>
    </>
}

// Per anomenar i reanomenar presets
// (name és un verb en aquest cas i em confón una mica, encara que no he trobat una millor alternativa)
function NamePresetDialog({isVisible, setIsVisible, presetName, presetID, savedPresetsForInstrumentType}) {
    const [tmpName, setTmpName] = useState("");
    const name = tmpName.trim();
    const overwrittenID = Object.entries(savedPresetsForInstrumentType).find(([id, {_metadata: {name: candidateName}}]) => id !== presetID && name === candidateName)?.[0];
    const inputRef = useRef();

    useEffect(() => {
        if (!isVisible) return;
        setTmpName(presetName);
        const timeout = setTimeout(() => inputRef.current.select(), 100);
        return () => clearTimeout(timeout);
    }, [isVisible]);

    function isNameValid() {
        if (name.length == 0) return false;
        if (Object.values(savedPresetsForInstrumentType)
            .filter(({_metadata}) => _metadata.isFactory)
            .map(({_metadata}) => _metadata.name)
            .includes(name)
        ) return false;
        if (name.endsWith("*")) return false; // aquest és el símbol que fem servir per presets sense guardar
        return true;
    }
    const dispatch = useDispatch();

    const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState(false);
    function confirm() {
        dispatch(savePreset({presetID, name, overwrittenID}));
        setIsVisible(false);
    }
    function commitPresetName() {
        if (!isNameValid()) return;
        if (overwrittenID != null) return setIsConfirmDialogVisible(true);
        confirm();
    }

    return <>
        <Dialog className="preset-save-dialog" visible={isVisible} onHide={()=>setIsVisible(false)} draggable={false} resizable={false}>
            <h2>Anomena el preset</h2>
            <input onKeyDown={e => e.key === "Enter" && commitPresetName()} ref={inputRef} value={tmpName} onChange={e => setTmpName(e.target.value)}></input>
            <button className="btn btn-white" disabled={!isNameValid()} onClick={commitPresetName}>OK</button>
        </Dialog>
        <Dialog className="confirm-dialog" visible={isConfirmDialogVisible} onHide={() => setIsConfirmDialogVisible(false)} draggable={false} resizable={false}>
            <h2>Confirma</h2>
            <p>Ja existeix un preset amb el nom "{name}". El vols sobrescriure?</p>
            <div className="confirm-btns">
                <button className="btn btn-white" onClick={() => {setIsConfirmDialogVisible(false)}}>No</button>
                <button className="btn btn-white" onClick={() => {setIsConfirmDialogVisible(false);confirm()}}>Sí</button>
            </div>
        </Dialog>
    </>
}

function PresetMenu({currentPresetID, currentPreset, instrumentID, showNamePresetDialog, ...props}) {
    const isCurrentPresetFactory = currentPreset._metadata.isFactory
    const isCurrentPresetMetadataModifiable = !(currentPreset._metadata.isUnsaved || isCurrentPresetFactory);
    const isDisabled = currentPresetID === "init";

    const [isOpen, setIsOpen] = useState(false);
    useEffect(() => {
        setIsOpen
    }, [isDisabled]);
    const dispatch = useDispatch();
    function initialize() {
        dispatch(loadPresetInInstrument({instrumentID, presetID: "init"}));
    }
    function exportCurrentPreset() {
        const preset = {...currentPreset, _metadata: {...currentPreset._metadata}};
        const name = preset._metadata.name;
        delete preset._metadata.isSaved;
        delete preset._metadata.isFactory;
        delete preset._metadata.name;
        downloadObjectAsJson(preset, name, ".grufpreset");
    }
    return (
        <Menu.Root open={isOpen} onOpenChange={setIsOpen}>
            <Menu.Trigger className="btn btn-white w-30" disabled={isDisabled} {...props}>
                <MenuIcon />
            </Menu.Trigger>
           <Menu.Portal>
                <Menu.Positioner align="start">
                    <Menu.Popup className="menu">
                        <Menu.Item><button onClick={initialize}>Carrega els valors per defecte</button></Menu.Item>
                        {!isCurrentPresetFactory && <Menu.Item><button onClick={exportCurrentPreset}>Exporta</button></Menu.Item>}
                        {isCurrentPresetMetadataModifiable && <Menu.Item><button onClick={showNamePresetDialog}>Reanomena</button></Menu.Item>}
                        {isCurrentPresetMetadataModifiable && <Menu.Item><button onClick={() => dispatch(deletePreset({presetID: currentPresetID}))}>Elimina</button></Menu.Item>}
                    </Menu.Popup>
                </Menu.Positioner>
            </Menu.Portal>
        </Menu.Root>
    )
}
